import React, { useEffect, useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { CheckCircle, TrendingUp, BookOpen, Heart, Target, Brain, Calendar, Users } from 'lucide-react';

const reportSections = [
  { icon: <Brain size={16} strokeWidth={1.5} />, title: 'Executive Summary', color: '#2A9D8F', preview: 'Aryan demonstrates strong analytical reasoning with an above-average capacity for structured problem-solving. His learning orientation is primarily conceptual with a secondary preference for visual cues.' },
  { icon: <TrendingUp size={16} strokeWidth={1.5} />, title: 'Strength Areas', color: '#8DA399', items: ['Critical Thinking (92/100)', 'Emotional Intelligence (84/100)', 'Analytical Reasoning (88/100)', 'Adaptability (79/100)'] },
  { icon: <Target size={16} strokeWidth={1.5} />, title: 'Career Alignment', color: '#F4A261', items: ['Research & Academia', 'Product Management', 'Psychology / Counseling', 'Data Science'] },
  { icon: <BookOpen size={16} strokeWidth={1.5} />, title: 'Learning Strategy', color: '#2A9D8F', preview: 'Recommend spaced repetition with visual mapping techniques. Short focused sessions (25-minute Pomodoro) with reflective journaling after concept blocks.' },
  { icon: <Heart size={16} strokeWidth={1.5} />, title: 'Emotional Regulation', color: '#8DA399', preview: 'Identified tendency toward rumination under stress. Breathing anchor techniques and structured journaling recommended.' },
  { icon: <Calendar size={16} strokeWidth={1.5} />, title: 'Parent Guidance', color: '#F4A261', preview: 'Focus conversations on strengths rather than grades. Allow exploration in areas of interest. Seek counselor guidance before making stream-change decisions.' },
];

const StrengthBar = ({ label, value, color }) => (
  <div style={{ marginBottom: '10px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
      <span style={{ fontSize: '12px', fontFamily: 'Manrope', fontWeight: 500, color: '#4A5568' }}>{label}</span>
      <span style={{ fontSize: '12px', fontFamily: 'Manrope', fontWeight: 700, color }}>
        {value}%
      </span>
    </div>
    <div style={{ height: '6px', background: 'rgba(0,0,0,0.05)', borderRadius: '99px', overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${value}%`, borderRadius: '99px',
        background: `linear-gradient(90deg, ${color}, ${color}88)`,
        transition: 'width 1s ease',
      }} />
    </div>
  </div>
);

const ReportPreview = () => {
  const [ref, visible] = useScrollReveal();
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTab(prev => (prev + 1) % reportSections.length);
    }, 2000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      id="reports"
      ref={ref}
      style={{ padding: '100px 24px', background: 'rgba(240,244,242,0.5)' }}
      data-testid="report-preview-section"
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div className={`reveal ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: '64px' }}>
          <div className="section-label">AI Advisory Report</div>
          <div className="section-divider" />
          <h2 style={{
            fontFamily: 'Manrope', fontWeight: 700, fontSize: 'clamp(30px,3.8vw,46px)',
            color: '#1A202C', margin: '20px 0 16px', letterSpacing: '-0.02em',
          }}>
            What You <span style={{ color: '#2A9D8F' }}>Receive</span>
          </h2>
          <p style={{ fontSize: '16px', color: '#64748B', maxWidth: '480px', margin: '0 auto', lineHeight: 1.7 }}>
            A comprehensive, intelligent advisory report that turns complex data into a clear roadmap — for students and parents alike.
          </p>
        </div>

        <div className={`reveal ${visible ? 'visible' : ''}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '40px', alignItems: 'start' }}>

          {/* Left - section menu */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {reportSections.map((s, i) => (
              <button
                key={s.title}
                onClick={() => setActiveTab(i)}
                data-testid={`report-tab-${i}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '14px 18px', borderRadius: '14px', cursor: 'pointer',
                  border: activeTab === i ? `1.5px solid ${s.color}40` : '1.5px solid transparent',
                  background: activeTab === i ? `${s.color}0D` : 'rgba(255,255,255,0.5)',
                  transition: 'all 0.2s ease', textAlign: 'left',
                }}
              >
                <div style={{
                  width: '34px', height: '34px', borderRadius: '10px',
                  background: `${s.color}15`, color: s.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>{s.icon}</div>
                <span style={{
                  fontFamily: 'Manrope', fontWeight: activeTab === i ? 600 : 400,
                  fontSize: '14px', color: activeTab === i ? '#1A202C' : '#64748B',
                }}>{s.title}</span>
                {activeTab === i && (
                  <div style={{ marginLeft: 'auto', width: '6px', height: '6px', borderRadius: '50%', background: s.color }} />
                )}
              </button>
            ))}
          </div>

          {/* Right - Report card mock */}
          <div
            style={{
              background: 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(42,157,143,0.15)',
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(42,157,143,0.12), 0 6px 20px rgba(0,0,0,0.05)',
            }}
          >
            {/* Report header */}
            <div style={{
              padding: '24px 32px',
              background: 'linear-gradient(135deg, rgba(42,157,143,0.08), rgba(141,163,153,0.06))',
              borderBottom: '1px solid rgba(42,157,143,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#2A9D8F', marginBottom: '4px' }}>
                  MindVrik · Advisory Report
                </div>
                <div style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '18px', color: '#1A202C' }}>Aryan K. · Class 11</div>
                <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>Generated: February 2026 · Science Stream</div>
              </div>
              <div style={{
                width: '60px', height: '60px', borderRadius: '16px',
                background: 'linear-gradient(135deg, #2A9D8F, #228B7E)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                color: 'white',
              }}>
                <div style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: '18px', lineHeight: 1 }}>82</div>
                <div style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.06em' }}>SCORE</div>
              </div>
            </div>

            {/* Report body - dynamic tab content */}
            <div style={{ padding: '28px 32px', minHeight: '280px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '10px',
                  background: `${reportSections[activeTab].color}15`,
                  color: reportSections[activeTab].color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{reportSections[activeTab].icon}</div>
                <h4 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '16px', color: '#1A202C', margin: 0 }}>
                  {reportSections[activeTab].title}
                </h4>
              </div>

              {activeTab === 1 ? (
                /* Strength bars */
                <div>
                  <StrengthBar label="Critical Thinking" value={92} color="#2A9D8F" />
                  <StrengthBar label="Emotional Intelligence" value={84} color="#8DA399" />
                  <StrengthBar label="Analytical Reasoning" value={88} color="#2A9D8F" />
                  <StrengthBar label="Adaptability" value={79} color="#F4A261" />
                  <StrengthBar label="Creative Thinking" value={72} color="#8DA399" />
                </div>
              ) : activeTab === 2 ? (
                /* Career chips */
                <div>
                  <p style={{ fontSize: '13px', color: '#64748B', marginBottom: '16px', lineHeight: 1.6 }}>
                    Based on your assessment profile, these career domains show strong natural alignment:
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {reportSections[activeTab].items.map((item, idx) => (
                      <span key={item} style={{
                        padding: '8px 16px', borderRadius: '99px',
                        background: idx === 0 ? 'rgba(42,157,143,0.12)' : 'rgba(42,157,143,0.06)',
                        border: `1px solid ${idx === 0 ? 'rgba(42,157,143,0.3)' : 'rgba(42,157,143,0.15)'}`,
                        fontSize: '13px', fontFamily: 'Manrope', fontWeight: idx === 0 ? 600 : 400,
                        color: '#2A9D8F',
                        display: 'flex', alignItems: 'center', gap: '6px'
                      }}>
                        {idx === 0 && <CheckCircle size={12} />}{item}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                /* Text preview */
                <div>
                  <p style={{ fontSize: '14px', color: '#4A5568', lineHeight: 1.75, margin: '0 0 20px' }}>
                    {reportSections[activeTab].preview}
                  </p>
                  <div style={{
                    padding: '14px 18px', borderRadius: '12px',
                    background: 'rgba(42,157,143,0.06)',
                    border: '1px solid rgba(42,157,143,0.15)',
                    fontSize: '12px', color: '#64748B', lineHeight: 1.6
                  }}>
                    This section contains AI-generated guidance based on validated assessment data. Always discuss with your assigned counselor for personalised interpretation.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReportPreview;
